import React, { useMemo } from 'react';
import { isValidCoordinate, getCoordinateValue } from '../utils/validation';
import { GeocodingResult, GeocodingError, SearchProgress } from '../types';

interface ResultsListProps {
  results: GeocodingResult[];
  errors: GeocodingError[];
  onResultHover?: ((index: number, hover: boolean) => void) | null;
  onResultClick?: ((result: GeocodingResult, index: number) => void) | null;
  onCorrectAddress?: ((result: GeocodingResult, index: number) => void) | null;
  onRetryError?: ((error: GeocodingError) => void) | null;
  isSearching?: boolean;
  searchProgress?: SearchProgress | null;
}

const ResultsList: React.FC<ResultsListProps> = ({
  results = [],
  errors = [],
  onResultHover = null,
  onResultClick = null,
  onCorrectAddress = null,
  onRetryError = null,
  isSearching = false,
  searchProgress = null
}) => {
  const handleResultHover = (index: number, hover: boolean) => {
    if (onResultHover) {
      onResultHover(index, hover);
    }
  };

  const handleResultClick = (result: GeocodingResult, index: number) => {
    if (onResultClick) {
      onResultClick(result, index);
    }
  };

  const handleCorrectClick = (result: GeocodingResult, index: number) => {
    if (onCorrectAddress) {
      onCorrectAddress(result, index);
    }
  };

  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => (
      (typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER) -
      (typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER)
    ));
  }, [results]);

  const sortedErrors = useMemo(() => {
    return [...errors].sort((a, b) => (
      (typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER) -
      (typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER)
    ));
  }, [errors]);

  const exportResults = () => {
    if (sortedResults.length === 0) return;

    const csvContent = [
      ['原始地址', '完整地址', '格式化地址', '经度', '纬度', '省份', '城市', '区县'].join(','),
      ...sortedResults.map(result => [
        `"${result.originalAddress}"`,
        `"${result.fullAddress}"`,
        `"${result.formattedAddress}"`,
        getCoordinateValue(result.location, 'lng'),
        getCoordinateValue(result.location, 'lat'),
        `"${result.addressComponent?.province || ''}"`,
        `"${result.addressComponent?.city || ''}"`,
        `"${result.addressComponent?.district || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `地址解析结果_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="results-panel">
      <div className="results-header">
        <h3 className="results-title">搜索结果</h3>

        {(sortedResults.length > 0 || sortedErrors.length > 0) && (
          <div className="results-actions">
            <button
              className="btn btn-outline btn-sm"
              onClick={exportResults}
              disabled={sortedResults.length === 0}
            >
              导出CSV
            </button>
          </div>
        )}
      </div>

      {searchProgress && (
        <div className="search-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(searchProgress.current / searchProgress.total) * 100}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {searchProgress.current}/{searchProgress.total}
            (成功: {searchProgress.success}, 失败: {searchProgress.errors})
          </div>
        </div>
      )}

      <div className="results-content">
        {sortedResults.length === 0 && sortedErrors.length === 0 && !searchProgress && (
          <div className="results-empty">
            <div className="empty-icon">📍</div>
            <div className="empty-text">暂无搜索结果</div>
            <div className="empty-description">
              请在上方输入地址并点击搜索
            </div>
          </div>
        )}

        {sortedResults.length > 0 && (
          <div className="results-section">
            <div className="section-header">
              <h4 className="section-title">成功解析 ({sortedResults.length})</h4>
            </div>
            <div className="results-list">
              {sortedResults.map((result, index) => (
                <div
                  key={index}
                  className="result-item"
                  onMouseEnter={() => handleResultHover(index, true)}
                  onMouseLeave={() => handleResultHover(index, false)}
                  onClick={() => handleResultClick(result, index)}
                >
                  <div className="result-header">
                    <div className="result-number">
                      {typeof result.order === 'number' ? result.order + 1 : index + 1}
                    </div>
                    <div className="result-address">
                      <div className="original-address">{result.originalAddress}</div>
                      <div className="formatted-address">{result.formattedAddress}</div>
                    </div>
                    <div className="result-actions">
                      {result.candidates && result.candidates.length > 0 && (
                        <button
                          className="btn btn-outline btn-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCorrectClick(result, index);
                          }}
                        >
                          修正
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="result-details">
                    <div className="coordinate">
                      {isValidCoordinate(result.location) ? (
                        <>经度: {result.location!.lng.toFixed(6)}, 纬度: {result.location!.lat.toFixed(6)}</>
                      ) : (
                        <span className="coordinate-error">坐标无效</span>
                      )}
                    </div>
                    {result.addressComponent && (
                      <div className="address-component">
                        {result.addressComponent.province}
                        {result.addressComponent.city}
                        {result.addressComponent.district}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedErrors.length > 0 && (
          <div className="results-section errors">
            <div className="section-header">
              <h4 className="section-title error">解析失败 ({sortedErrors.length})</h4>
            </div>
            <div className="error-list">
              {sortedErrors.map((error, index) => (
                <div key={index} className={`error-item${error.retrying ? ' retrying' : ''}`}>
                  <div className="error-address">
                    <div className="error-original">{error.originalAddress || error.address}</div>
                    {error.originalAddress && error.originalAddress !== error.address && (
                      <div className="error-full">完整地址: {error.address}</div>
                    )}
                  </div>
                  <div className="error-message">
                    {error.error}
                    {error.code && error.code !== 'UNKNOWN' && (
                      <span className="error-code">({error.code})</span>
                    )}
                  </div>
                  {error.canRetry !== false && (
                    <div className="error-actions">
                      <button
                        className="btn btn-outline btn-xs"
                        onClick={() => onRetryError && onRetryError(error)}
                        disabled={isSearching || error.retrying}
                      >
                        {error.retrying ? '重试中...' : '重试'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsList;
